import { useMemo } from "react";
import { Marked } from "marked";
import { MathContent } from "@/components/math/math-content";

interface TheoryViewProps {
  html: string;
  /** Extra classes appended after the shared prose styles (e.g. color tint). */
  className?: string;
  /** Optional max height for scrollable container. */
  maxHeight?: string;
  /**
   * Keep the file's own section order. Admin views preview what is stored;
   * everything a child reads gets the example first.
   */
  sourceOrder?: boolean;
  /** Render only the worked example — the "show me one" peek mid-question. */
  exampleOnly?: boolean;
  /**
   * Render the concept card: this question's example, the explanation behind it,
   * and the rule to remember. See `buildConceptCard`.
   */
  conceptOnly?: boolean;
  /**
   * The concepts of the question being asked. With `exampleOnly`, picks the
   * example that teaches one of them instead of whichever comes first — a child
   * stuck on a two-step equation was being shown addition. With `conceptOnly`,
   * it is what the whole card is narrowed to.
   */
  concepts?: string[];
  /** Render only "Svarbu prisiminti" — the closing summary after the work. */
  keyPointsOnly?: boolean;
  /** Figures in the theory open full screen, as the question's do. */
  zoomableFigures?: boolean;
}

// Sandboxed Marked instance: GFM tables, line breaks within paragraphs.
const marked = new Marked({ gfm: true, breaks: false });

// Decode HTML entities that get introduced when markdown source survives an
// HTML round-trip (e.g. `|` inside a table appears as `&#124;` after parsing).
function decodeEntities(text: string): string {
  const el = document.createElement("textarea");
  el.innerHTML = text;
  return el.value;
}

// Strip the common leading whitespace from non-empty lines. The Ruby theory
// composer interpolates markdown into indented HTML scaffolding, which leaves
// the first line prefixed with spaces — enough to trip markdown's 4-space
// "code block" rule and wrap the opening paragraph in <pre><code>.
function dedent(source: string): string {
  const lines = source.split("\n");
  let min = Infinity;
  for (const line of lines) {
    if (!line.trim()) continue;
    const match = line.match(/^[ \t]*/);
    const indent = match ? match[0].length : 0;
    if (indent < min) min = indent;
  }
  if (!isFinite(min) || min === 0) return source.replace(/^\s+|\s+$/g, "");
  return lines.map((l) => l.slice(min)).join("\n").replace(/^\s+|\s+$/g, "");
}

// Nokogiri's serializer (ExerciseLoader#load_theory_html) re-wraps lines inside
// inline elements, so legacy HTML-only theory arrives with some lines pushed to
// column 0. That makes the min-indent above 0, leaving the rest of the markup
// indented by 4+ spaces — which markdown reads as an indented code block and
// renders as literal `<p>`/`<ul>` text. Markup lines never need indentation, so
// drop theirs; markdown lines keep theirs (nested lists, indented code).
function unindentMarkupLines(source: string): string {
  return source.replace(/^[ \t]+(?=<)/gm, "");
}

function renderMarkdownBlock(source: string): string {
  const normalized = unindentMarkupLines(dedent(decodeEntities(source)));
  return marked.parse(normalized, { async: false }) as string;
}

function renderMarkdownInline(source: string): string {
  return marked.parseInline(decodeEntities(source), { async: false }) as string;
}

/**
 * Show, then name: the worked example moves ahead of the explanation.
 *
 * Theory files open with several paragraphs of prose and put the solved example
 * after them, which is backwards for a child meeting a topic for the first time
 * — there is nothing in their head yet for the prose to attach to. A concrete
 * example first gives the words something to be about.
 */
function moveExampleFirst(doc: Document): void {
  const example = doc.querySelector(".example");
  const concept = doc.querySelector(".concept");
  if (!example || !concept || example === concept) return;

  // Already ahead of the explanation — nothing to do.
  const conceptFollowsExample =
    example.compareDocumentPosition(concept) & Node.DOCUMENT_POSITION_FOLLOWING;
  if (conceptFollowsExample) return;

  concept.parentNode?.insertBefore(example, concept);
}

/**
 * The worked example to show a child stuck on THIS question.
 *
 * Theory that teaches five things carries five examples, each tagged with the
 * concept it demonstrates (`data-concept`, written by TheoryConcepts). Showing
 * the first one regardless meant a child stuck on a two-step equation was handed
 * an addition example — worse than nothing, because it looks like an answer.
 *
 * Falls back to the first example whenever there is nothing to match on: an
 * untagged question, untagged theory, or a concept no example covers. That is the
 * old behaviour, and it is right — one example is better than none.
 */
function pickExample(doc: Document, concepts: string[]): Element | null {
  const examples = Array.from(doc.querySelectorAll(".example"));
  if (examples.length === 0) return null;
  if (examples.length === 1 || concepts.length === 0) return examples[0];

  // Walk the QUESTION's concepts in order, not the examples': the tagger puts a
  // question's primary concept first, and a word problem that also happens to be
  // a two-step equation is a child stuck on building the equation. Iterating the
  // examples instead would hand them whichever match appeared earliest in the
  // theory, which is arbitrary.
  for (const concept of concepts) {
    const match = examples.find((example) => conceptsOf(example).has(concept));
    if (match) return match;
  }

  return examples[0];
}

/** The concept keys a theory block is tagged with (`data-concept`). */
function conceptsOf(el: Element): Set<string> {
  return new Set((el.getAttribute("data-concept") || "").split(/\s+/).filter(Boolean));
}

/**
 * The one small thing to read when stuck on THIS question: its worked example,
 * the explanation of the concept behind it, then the rule to remember — in that
 * order, composed from the skill's single theory file.
 *
 * Deliberately not "the theory page, scrolled to the right paragraph". That page
 * already auto-opens for a new topic and was read on 24% of the tasks offering
 * it; highlighting a region inside it still asks a child to be in it, and with
 * `.concept` prose running to a median ~1000 characters the highlighted part is
 * often off-screen — so the child scrolls to hunt for their own help, having left
 * the answer field behind. This card cannot overshoot: one example, one
 * explanation, one rule, arriving under the question.
 *
 * Composed at render time rather than generated as per-concept files. The theory
 * file stays the single source of truth, so the card can never drift from what
 * the theory page shows, and a skill gains this the moment its theory is tagged —
 * no second artifact to generate, review, or keep in sync.
 *
 * **The prose is included only where it can be narrowed.** 388 of 389 theory
 * files predate concept tagging; falling back to the whole `.concept` block would
 * hand the child exactly the wall this exists to avoid. An untagged skill gets
 * the example alone — which is what it already had.
 *
 * Returns false when there is nothing at all to show.
 */
function buildConceptCard(doc: Document, concepts: string[]): boolean {
  const parts: Element[] = [];

  const example = pickExample(doc, concepts);
  if (example) parts.push(example);

  const tagged = (el: Element) => concepts.some((concept) => conceptsOf(el).has(concept));

  if (concepts.length > 0) {
    for (const part of Array.from(doc.querySelectorAll(".concept-part"))) {
      if (tagged(part)) parts.push(part);
    }

    const points = Array.from(doc.querySelectorAll(".key-points li")).filter(tagged);
    if (points.length > 0) {
      // Kept wrapped in .key-points: the markdown pass below and the shared
      // stylesheet both key off that class, and the heading is what marks the
      // line as the rule to remember rather than more explanation.
      const section = doc.createElement("div");
      section.className = "key-points";
      const heading = doc.querySelector(".key-points h3");
      if (heading) section.appendChild(heading.cloneNode(true));
      const list = doc.createElement("ul");
      for (const li of points) list.appendChild(li);
      section.appendChild(list);
      parts.push(section);
    }
  }

  if (parts.length === 0) return false;

  doc.body.replaceChildren(...parts);
  return true;
}

/**
 * Theory files are stored as HTML scaffolding (see TheoryComposition in Ruby)
 * with markdown source inside specific containers (concept, step-content, list
 * items, etc.). This walker upgrades those containers to rendered HTML so the
 * same DOM can render both legacy HTML-only theory and new markdown theory, and
 * on the way decides which sections a reader gets and in what order.
 */
type RenderOptions = {
  sourceOrder?: boolean;
  exampleOnly?: boolean;
  conceptOnly?: boolean;
  keyPointsOnly?: boolean;
  concepts?: string[];
};

function renderMarkdownContainers(
  html: string,
  {
    sourceOrder = false,
    exampleOnly = false,
    conceptOnly = false,
    keyPointsOnly = false,
    concepts = []
  }: RenderOptions = {}
): string {
  if (typeof DOMParser === "undefined") return html;

  const doc = new DOMParser().parseFromString(html, "text/html");

  if (exampleOnly) {
    const example = pickExample(doc, concepts);
    if (!example) return "";
    doc.body.replaceChildren(example);
  } else if (conceptOnly) {
    if (!buildConceptCard(doc, concepts)) return "";
  } else if (keyPointsOnly) {
    const section = doc.querySelector(".key-points");
    if (!section) return "";
    doc.body.replaceChildren(section);
  } else if (!sourceOrder) {
    moveExampleFirst(doc);
  }

  // A tagged explanation is split into .concept-part blocks (one per concept it
  // teaches), and the markdown lives inside those. Rendering the .concept wrapper
  // instead would hand marked a block-level <div>, which it passes through
  // untouched — the markdown inside would reach the child as literal `**text**`.
  for (const el of Array.from(
    doc.querySelectorAll(".concept, .concept-part, .step-content, .problem, .answer")
  )) {
    if (el.classList.contains("concept") && el.querySelector(".concept-part")) continue;

    el.innerHTML = renderMarkdownBlock(el.innerHTML);
  }

  for (const li of Array.from(
    doc.querySelectorAll(".key-points li, .common-mistakes li")
  )) {
    li.innerHTML = renderMarkdownInline(li.innerHTML);
  }

  // Strip the <html>/<head>/<body> wrapper that DOMParser adds.
  return doc.body.innerHTML;
}

// Most of the visual treatment lives in globals.css under `.theory-content`
// (see "Theory rendering" block). Inline classes here cover only what the
// shared stylesheet doesn't touch — heading sizes inside markdown bodies and
// hiding the duplicated theory title.
const PROSE_CLASSES =
  "theory-content prose prose-sm dark:prose-invert max-w-none " +
  "[&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 " +
  "[&_h3]:text-lg [&_h3]:font-bold [&_h3]:mt-4 [&_h3]:mb-2 " +
  "[&_h4]:text-base [&_h4]:font-semibold [&_h4]:mt-3 [&_h4]:mb-1.5 " +
  "[&_p]:my-2 [&_p]:leading-relaxed " +
  "[&_strong]:font-semibold [&_em]:italic " +
  "[&_.theory-title]:hidden";

function hasSection(html: string | null | undefined, selector: string): boolean {
  if (!html || typeof DOMParser === "undefined") return false;

  return new DOMParser().parseFromString(html, "text/html").querySelector(selector) !== null;
}

/** Does this theory carry a worked example worth offering mid-question? */
export function hasTheoryExample(html?: string | null): boolean {
  return hasSection(html, ".example");
}

/** Does it carry "Svarbu prisiminti", for the summary after the work? */
export function hasTheoryKeyPoints(html?: string | null): boolean {
  return hasSection(html, ".key-points");
}

/**
 * Is there anything in this theory the concept card leaves out?
 *
 * What gates „Rodyti visą teoriją" on the card. The card is a narrowing, so on a
 * theory file that is nothing but one worked example it narrows to the whole
 * file — and a button that visibly does nothing is worse than no button, because
 * the child presses it while stuck and learns the app is decoration.
 */
export function hasTheoryBeyondExample(html?: string | null): boolean {
  if (!html || typeof DOMParser === "undefined") return false;

  const doc = new DOMParser().parseFromString(html, "text/html");
  if (doc.querySelectorAll(".example").length > 1) return true;

  return doc.querySelector(".concept, .key-points, .common-mistakes") !== null;
}

export function TheoryView({
  html,
  className = "",
  maxHeight,
  sourceOrder = false,
  exampleOnly = false,
  conceptOnly = false,
  keyPointsOnly = false,
  concepts,
  zoomableFigures = false
}: TheoryViewProps) {
  // Joined for the dependency list: a new array of the same keys on every render
  // would rebuild the DOM each time.
  const conceptKey = (concepts || []).join(" ");
  const rendered = useMemo(
    () =>
      renderMarkdownContainers(html, {
        sourceOrder,
        exampleOnly,
        conceptOnly,
        keyPointsOnly,
        concepts: conceptKey ? conceptKey.split(" ") : []
      }),
    [html, sourceOrder, exampleOnly, conceptOnly, keyPointsOnly, conceptKey]
  );
  const style = maxHeight ? { maxHeight, overflowY: "auto" as const } : undefined;
  return (
    <MathContent
      html={rendered}
      className={`${PROSE_CLASSES} ${className}`}
      style={style}
      zoomableFigures={zoomableFigures}
    />
  );
}
